import { Brand, BrandLogo, Color } from '@anm/api/modules/brands';
import { SelectOption } from '@anm/components/select/Select';
import { DEFAULT_WAVE_COLOR } from '@anm/constants/colors';
import { LogoItem } from '@anm/shared/types/branding';

type GetBrandData = {
  brands: Brand[];
  brandId: number;
};

export const getLogosList = (logoList?: BrandLogo[]) =>
  logoList?.map(
    l =>
      ({
        id: l.id,
        name: l.filename,
        iconSrc: l.preview,
        dimension: l.dimension
      } as LogoItem)
  ) || null;

export const mapBrandToSelectOption = (brand: Brand): SelectOption<number> => ({
  value: brand.id,
  label: brand.name,
  icon: getBrandLogos(brand)?.[0]?.iconSrc
});

export const getSelectedBrand = ({ brands, brandId }: GetBrandData) => brands.find(brand => brand.id === brandId);

export const getBrandLogos = (brand: Brand | null) => getLogosList(brand?.logos);

export const getBrandOptions = (brands: Brand[]) => brands.map(brand => mapBrandToSelectOption(brand));

export const getBrandColors = (brand: Brand | null) => brand?.colors;

export const getBrandMappedColor = (color?: Color) => color?.fill.color.toUpperCase();

const getBrandsWithReversedLogos = (brands: Brand[]): Brand[] =>
  brands.map(b => ({
    ...b,
    logos: b.logos.reverse()
  }));

export const getAllowedBrands = (brands: Brand[]): Brand[] => {
  const allowedBrands = brands.filter(b => !(b.meta.readonly || b.meta.hidden));
  return getBrandsWithReversedLogos(allowedBrands);
};

export const getNewBrands = (newBrand: Brand, brands: Brand[]): Brand[] => {
  const newBrands = [...brands];
  const newBrandId = newBrand.id;
  const brandIndex = newBrands.findIndex(b => b.id === newBrandId)!;
  newBrands[brandIndex] = getBrandsWithReversedLogos([newBrand])[0];

  return newBrands;
};

export const getUploadedLogoPreview = (newBrand: Brand) => {
  const brandLogos = newBrand.logos;
  const logosLength = brandLogos.length;

  return brandLogos[logosLength - 1].preview;
};

export const updateBrandList = (brands: Brand[], currentBrandId: number, deletedLogoId?: string) => {
  const updatedBrands = [...brands];
  const brandIndex = brands.findIndex(b => b.id === currentBrandId);
  const brandToUpdate = updatedBrands[brandIndex];
  updatedBrands[brandIndex] = {
    ...brandToUpdate,
    logos: brandToUpdate.logos.filter(l => l.id !== deletedLogoId)
  };

  return updatedBrands;
};

export const getBrandFirstColor = (currentBrand: Brand) => {
  const brandColors = getBrandColors(currentBrand);
  const firstBrandColor = brandColors?.[0]?.fill.color;

  const brandColor = firstBrandColor || DEFAULT_WAVE_COLOR;

  return brandColor;
};

export default getBrandFirstColor;
